import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Code,
  Heading,
  HStack,
  NativeSelect,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { listAuditEvents, listUsers, type AuditEvent, type AdminUser } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import {
  DateRangePicker,
  rangeFor,
  type Preset,
} from '@/components/dashboard/DateRangePicker';

const PAGE_SIZE = 50;

const ENTITY_LABEL: Record<string, string> = {
  user: 'Usuario',
  project: 'Proyecto',
  client: 'Cliente',
  client_project: 'Vínculo cliente-proyecto',
  contract: 'Contrato',
  payment: 'Pago',
  payment_receipt: 'Comprobante',
  project_settings: 'Configuración',
};

const ACTION_LABEL: Record<string, string> = {
  create: 'Creación',
  update: 'Edición',
  archive: 'Archivado',
  unarchive: 'Restaurado',
  link: 'Vinculación',
  cancel: 'Cancelación',
  register: 'Registro',
  upload: 'Carga',
  settings_update: 'Config.',
  titling_stage_changed: 'Escrituración',
};

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-GT', { dateStyle: 'medium', timeStyle: 'short' });
}

function detailText(ev: AuditEvent): string {
  const parts: string[] = [];
  if (ev.before != null) parts.push(`antes: ${JSON.stringify(ev.before)}`);
  if (ev.after != null) parts.push(`después: ${JSON.stringify(ev.after)}`);
  return parts.join('\n');
}

export function AuditLog() {
  const { activeProject } = useProject();
  const [params, setParams] = useSearchParams();
  const preset = (params.get('preset') as Preset) || 'thisMonth';
  const entityType = params.get('entityType') ?? '';
  const action = params.get('action') ?? '';
  const actorUserId = params.get('actor') ?? '';
  const projectScope = params.get('scope') ?? activeProject?.id ?? '';
  const page = Math.max(0, Number(params.get('page') ?? '0'));

  const range = useMemo(() => {
    const from = params.get('from');
    const to = params.get('to');
    return from && to ? { from, to } : rangeFor(preset);
  }, [params, preset]);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [data, setData] = useState<{ rows: AuditEvent[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    listUsers().then(setUsers).catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAuditEvents({
      entityType: entityType || undefined,
      action: action || undefined,
      actorUserId: actorUserId || undefined,
      projectId: projectScope || undefined,
      from: range.from,
      to: range.to,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'No se pudo cargar la bitácora');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, action, actorUserId, projectScope, range.from, range.to, page]);

  function setParam(key: string, value: string) {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    p.delete('page');
    setParams(p, { replace: true });
  }

  function updateRange(next: { preset: Preset; from: string; to: string }) {
    const p = new URLSearchParams(params);
    p.set('preset', next.preset);
    p.set('from', next.from);
    p.set('to', next.to);
    p.delete('page');
    setParams(p, { replace: true });
  }

  function goPage(next: number) {
    const p = new URLSearchParams(params);
    p.set('page', String(next));
    setParams(p, { replace: true });
  }

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="6xl">
        <Stack gap="1">
          <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
            Auditoría
          </Heading>
          <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
            Bitácora de cambios sobre proyectos, clientes, contratos, pagos y usuarios.
          </Text>
        </Stack>

        <Stack direction={{ base: 'column', md: 'row' }} gap="3" flexWrap="wrap" align={{ md: 'center' }}>
          <NativeSelect.Root size="sm" maxW="48">
            <NativeSelect.Field
              aria-label="Tipo de entidad"
              value={entityType}
              onChange={(e) => setParam('entityType', e.currentTarget.value)}
            >
              <option value="">Todas las entidades</option>
              {Object.entries(ENTITY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          <NativeSelect.Root size="sm" maxW="44">
            <NativeSelect.Field
              aria-label="Acción"
              value={action}
              onChange={(e) => setParam('action', e.currentTarget.value)}
            >
              <option value="">Todas las acciones</option>
              {Object.entries(ACTION_LABEL).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          <NativeSelect.Root size="sm" maxW="56">
            <NativeSelect.Field
              aria-label="Actor"
              value={actorUserId}
              onChange={(e) => setParam('actor', e.currentTarget.value)}
            >
              <option value="">Todos los usuarios</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.fullName}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          <NativeSelect.Root size="sm" maxW="56">
            <NativeSelect.Field
              aria-label="Proyecto"
              value={projectScope}
              onChange={(e) => setParam('scope', e.currentTarget.value)}
            >
              <option value="">Todos los proyectos</option>
              {activeProject && (
                <option value={activeProject.id}>{activeProject.code} · {activeProject.name}</option>
              )}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          <DateRangePicker preset={preset} from={range.from} to={range.to} onChange={updateRange} />
        </Stack>

        {error && <Text color="red.600" fontSize="sm">{error}</Text>}

        {loading && !data ? (
          <HStack gap="3" color="fg.muted"><Spinner size="sm" /><Text fontSize="sm">Cargando…</Text></HStack>
        ) : rows.length === 0 ? (
          <Box bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="10" textAlign="center">
            <Text color="fg.muted">No hay eventos para los filtros seleccionados.</Text>
          </Box>
        ) : (
          <>
            {/* Card stack at base */}
            <Stack gap="3" display={{ base: 'flex', md: 'none' }}>
              {rows.map((ev) => {
                const detail = detailText(ev);
                return (
                  <Box key={ev.id} bg="bg.surface" borderWidth="1px" borderColor="border" rounded="lg" p="4">
                    <HStack justify="space-between" mb="1">
                      <Badge colorPalette="brand">{ACTION_LABEL[ev.action] ?? ev.action}</Badge>
                      <Text fontSize="xs" color="fg.muted">{fmtDateTime(ev.createdAt)}</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="600">{ENTITY_LABEL[ev.entityType] ?? ev.entityType}</Text>
                    <Text fontSize="xs" color="fg.muted">{ev.actorName ?? ev.actorUserId}</Text>
                    {detail && (
                      <Code mt="2" display="block" whiteSpace="pre-wrap" fontSize="xs" p="2" rounded="md">
                        {detail}
                      </Code>
                    )}
                  </Box>
                );
              })}
            </Stack>

            {/* Table at md+ */}
            <Box display={{ base: 'none', md: 'block' }} overflowX="auto">
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Fecha/hora</Table.ColumnHeader>
                    <Table.ColumnHeader>Actor</Table.ColumnHeader>
                    <Table.ColumnHeader>Acción</Table.ColumnHeader>
                    <Table.ColumnHeader>Entidad</Table.ColumnHeader>
                    <Table.ColumnHeader>Detalle</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {rows.map((ev) => {
                    const detail = detailText(ev);
                    return (
                      <Table.Row key={ev.id}>
                        <Table.Cell whiteSpace="nowrap">{fmtDateTime(ev.createdAt)}</Table.Cell>
                        <Table.Cell>{ev.actorName ?? ev.actorUserId}</Table.Cell>
                        <Table.Cell><Badge colorPalette="brand">{ACTION_LABEL[ev.action] ?? ev.action}</Badge></Table.Cell>
                        <Table.Cell>{ENTITY_LABEL[ev.entityType] ?? ev.entityType}</Table.Cell>
                        <Table.Cell>
                          {detail ? (
                            <Button size="xs" variant="ghost" onClick={() => setExpanded(expanded === ev.id ? null : ev.id)}>
                              {expanded === ev.id ? 'Ocultar' : 'Ver'}
                            </Button>
                          ) : (
                            <Text color="fg.subtle" fontSize="xs">—</Text>
                          )}
                          {expanded === ev.id && (
                            <Code mt="2" display="block" whiteSpace="pre-wrap" fontSize="xs" p="2" rounded="md">
                              {detail}
                            </Code>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table.Root>
            </Box>

            <HStack justify="space-between" pt="2">
              <Text fontSize="sm" color="fg.muted">
                {total} evento(s) · página {page + 1} de {maxPage + 1}
              </Text>
              <HStack gap="2">
                <Button size="xs" variant="outline" disabled={page <= 0} onClick={() => goPage(page - 1)}>
                  Anterior
                </Button>
                <Button size="xs" variant="outline" disabled={page >= maxPage} onClick={() => goPage(page + 1)}>
                  Siguiente
                </Button>
              </HStack>
            </HStack>
          </>
        )}
      </Stack>
    </Box>
  );
}
